import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { Globe, FileText, BarChart2, Grid, ChevronRight, Database, LineChart, Users, Package, Building2 } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useTheme } from '../ThemeContext';
interface NavItem {
  id: string;
  label: string;
  icon: React.ReactElement;
  path: string;
  subItems?: NavItem[];
}

export function MasterNav() {
  const collapsed = true;
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);
  const [hoveredPos, setHoveredPos] = useState<{ top: number; left: number } | null>(null);
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const symbol = location.pathname.split('/')[1];
  const currentPath = location.pathname;
  const appData = useSelector((state: any) => state.app);

  // Render icon with size depending on collapsed state
  const renderIcon = (icon: React.ReactElement, large: boolean) => {
    const baseClass = (icon.props as any).className || '';
    const sizeClass = large ? 'w-5 h-5' : '';
    const combined = `${sizeClass} ${baseClass}`.trim();
    return React.cloneElement(icon, { className: combined });
  };

  const navItems: NavItem[] = [{
    id: 'summary',
    label: 'Summary',
    icon: <Globe className="w-4 h-4" />,
    path: `/${symbol}/summary`
  }, {
    id: 'financials',
    label: 'Financial',
    icon: <FileText className="w-4 h-4" />,
    path: `/${symbol}/financials`,
    subItems: [{
      id: 'income-statement',
      label: 'Income Statement',
      icon: <FileText className="w-3.5 h-3.5" />,
      path: `/${symbol}/financials/income-statement`
    }, {
      id: 'balance-sheet',
      label: 'Balance Sheet',
      icon: <Database className="w-3.5 h-3.5" />,
      path: `/${symbol}/financials/balance-sheet`
    }, {
      id: 'cash-flow',
      label: 'Cash Flow',
      icon: <BarChart2 className="w-3.5 h-3.5" />,
      path: `/${symbol}/financials/cash-flow`
    }, {
      id: 'product-segments',
      label: 'Segments & KPI',
      icon: <Package className="w-3.5 h-3.5" />,
      path: `/${symbol}/financials/product-segments`
    }, {
      id: 'chart',
      label: 'Chart Analysis',
      icon: <LineChart className="w-3.5 h-3.5" />,
      path: `/${symbol}/financials/chart-analysis`
    }]
  }, {
    id: 'swot-analysis',
    label: 'SWOT Analysis',
    icon: <Grid className="w-4 h-4" />,
    path: `/${symbol}/swot-analysis`
  }, {
    id: 'forecast',
    label: 'Forecast',
    icon: <LineChart className="w-4 h-4" />,
    path: `/${symbol}/forecast`
  }, {
    id: 'analyst-corner',
    label: 'Analyst Corner',
    icon: <Users className="w-4 h-4" />,
    path: `/${symbol}/analyst-corner`
  }, {
    id: 'ownership',
    label: 'Ownership',
    icon: <Building2 className="w-4 h-4" />,
    path: `/${symbol}/ownership/institutions`
  }];

  const handleNavItemClick = (item: NavItem) => {
    // In collapsed mode, do not auto-navigate to first sub-item for parents with subItems.
    // Popover on hover is the primary interaction.
    if (collapsed && item.subItems && item.subItems.length > 0) {
      setHoveredSection(item.id);
      return;
    }
    if (item.subItems && item.subItems.length > 0) {
      setExpandedSection(expandedSection === item.id ? null : item.id);
      // If clicking on a section with subitems, navigate to the first subitem
      navigate(item.subItems[0].path);
    } else {
      navigate(item.path);
    }
  };

  const isItemActive = (path: string) => {
    return currentPath === path || currentPath.startsWith(path);
  };

  const isSubItemActive = (path: string) => {
    return currentPath === path;
  };

  return <div className={`
        bg-theme-primary h-full transition-all duration-300 ease-in-out relative overflow-y-auto scrollbar-hide
        w-14 border-r border-theme-secondary
      `}>
    <div className="px-0 py-0">
      {!collapsed && <div className="mb-6 pt-5">
          <h2 className="text-lg font-semibold text-theme-primary mb-2 flex items-center justify-center">
            <img src={appData.currentSymbolImage} alt="Symbol" className="w-6 h-6 inline-block mr-2 rounded-full" />
            {appData.currentSymbol || 'Symbol'}
          </h2>
          <div className="text-sm text-theme-muted text-center">
            {appData.currentCompanyName || 'Company Name'}
          </div>
        </div>}
      <nav className="space-y-0.5 pt-1" role="navigation" aria-label="Primary">
        {navItems.map(item => {
          const isActive = isItemActive(item.path);
          const isExpanded = expandedSection === item.id;
          return <div
            key={item.id}
            className="space-y-0.5 relative group"
            onMouseEnter={(e) => {
              if (item.subItems?.length) {
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                setHoveredPos({ top: rect.top + window.scrollY, left: rect.right + 8 + window.scrollX });
                setHoveredSection(item.id);
              } else {
                // Hovering an item without submenu should close any open submenu
                setHoveredSection(null);
                setHoveredPos(null);
              }
            }}
            onMouseLeave={() => { /* menu handles close to prevent clipping flicker */ }}
          >
            <button type="button" onClick={() => handleNavItemClick(item)} title={item.label} aria-label={item.label} aria-current={isActive ? 'page' : undefined} className={`
                    w-full flex items-center rounded-r-lg transition-all text-xs ring-1 ring-transparent relative outline-none
                    focus-visible:ring-2 focus-visible:ring-blue-500/50 focus-visible:ring-offset-0
                    ${collapsed ? 'flex-col items-center justify-center py-1.5 gap-0.5 px-0' : 'px-2 py-1.5'}
                    ${isActive
                      ? 'bg-blue-500/25 text-blue-400 ring-blue-500/40 border-l border-blue-500 shadow-[inset_2px_0_0_0_rgba(59,130,246,0.6)]'
                      : 'text-theme-secondary hover:bg-theme-secondary/80 hover:text-theme-primary hover:ring-gray-700/60'}
                  `}>
              <span className={`flex-shrink-0`}>{renderIcon(item.icon, collapsed)}</span>
              {!collapsed && <>
                <span className="ml-2 flex-grow text-left">
                  {item.label}
                </span>
                {item.subItems && item.subItems.length > 0 && <ChevronRight className={`w-3 h-3 transition-transform ${isExpanded ? 'transform rotate-90' : ''}`} />}
              </>}
              {collapsed && (
                <span className="mt-0.5 text-[10px] leading-none text-theme-secondary text-center w-full">
                  {item.label}
                </span>
              )}
            </button>
            {collapsed && null}
            {(item.subItems?.length ?? 0) > 0 && hoveredSection === item.id && hoveredPos && createPortal(
              <div
                className="w-56 bg-theme-secondary text-gray-100 rounded-md shadow-xl border border-theme-tertiary"
                style={{ position: 'fixed', top: hoveredPos.top, left: hoveredPos.left, zIndex: 9999 }}
                onMouseEnter={() => setHoveredSection(item.id)}
                onMouseLeave={() => { setHoveredSection(null); setHoveredPos(null); }}
              >
                {item.subItems?.map(subItem => (
                  <button
                    key={subItem.id}
                    onClick={(e) => { e.stopPropagation(); navigate(subItem.path); setHoveredSection(null); setHoveredPos(null); }}
                    className={`
                          w-full flex items-center px-3 py-2 rounded-md text-xs transition-colors
                          ${isSubItemActive(subItem.path) ? 'active-bg' : 'hover:bg-theme-tertiary text-submenu'}
                        `}
                  >
                    <span className={`flex-shrink-0 mr-2 `}>{subItem.icon}</span>
                    <span className={`flex-grow text-left `}>{subItem.label}</span>
                  </button>
                ))}
              </div>,
              document.body
            )}
          </div>;
        })}
      </nav>
    </div>
  </div>;
}